import {Injectable} from '@angular/core';
import {forkJoin, Observable, of, Subject} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';

import {Constants} from '../../constants';
import {NotificationService} from '../../apiservices/notification.service';
import {AlertService} from '../alert.service';
import {SubscribersService} from '../subscribers/subscribers.service';

/**
 * One change to a note that another surface may be showing.
 *
 * The same note is drawn by several components at once — the record screen's
 * notes card, the notification drawer in the header, the /notifications page —
 * and each holds its OWN copy from its own request. A like or a read/unread
 * change made in one never reached the others, so liking a note from the
 * drawer left the Sample Request card showing it unliked (and unread) until a
 * reload. Every surface now publishes what it changed here and patches its copy
 * from what the others publish.
 *
 * The payloads carry the RESULT, not a toggle, so applying one twice — the
 * emitter receives its own event too — is a no-op.
 */
export type NoteStateChange =
    | {
    kind: 'reaction';
    noteId: number;
    /** Who liked / unliked. */
    userId: number;
    liked: boolean;
    /** The server's reaction row on a like (it carries the id an unlike needs). */
    reaction?: any;
    /** The liker, for the "Liked by" list when the row has no `backend_user`. */
    user?: any;
}
    | {
    kind: 'read';
    /** The `notification` row whose status changed. */
    notificationId: number;
    /** `NOTIFICATION_STATUS_ACTIVE` (read) or `_INACTIVE` (unread). */
    status: number;
};

const sameId = (a: any, b: any): boolean => a != null && b != null && String(a) === String(b);

/** A note's notification rows (the API has used both spellings). */
function notificationsOf(note: any): any[] {
    return note?.notification || note?.notifications || [];
}

/**
 * The reader's own notifications about this note — what "read" means for a note.
 *
 * A note is unread for a user while a notification addressed to them about it
 * (a mention, or a reply to their note) is still unread. That is the rule the
 * Quote Details feed has always used (`extractMention`), so every surface now
 * answers it the same way.
 */
export function myNoteNotifications(note: any, userId: any): any[] {
    return notificationsOf(note).filter((n: any) => sameId(n?.to_user_id, userId));
}

/** Does the reader have anything to mark read / unread on this note? */
export function hasNoteReadState(note: any, userId: any): boolean {
    return myNoteNotifications(note, userId).length > 0;
}

/** Is this note unread for the reader? */
export function isNoteUnread(note: any, userId: any): boolean {
    return myNoteNotifications(note, userId)
        .some((n: any) => Number(n?.status) === Constants.NOTIFICATION_STATUS_INACTIVE);
}

/**
 * Who a reply to `note` should tag: the note's author — or, for Reply on a
 * reply, that reply's author. Quote Details' `initializeQuillEditor` /
 * `replyToSubNote` rule, as data for `<app-note-reply-editor [mentionUser]>`.
 *
 * Null only for a system note (no author). The reader's OWN note is tagged
 * too — Quote Details tags the author whoever they are, and the functionality
 * has to match it.
 */
export function replyMentionFor(note: any, _selfId?: any): { id: any; value: string } | null {
    const id = note?.backend_user_id ?? note?.backend_user?.id;
    if (id == null) {
        return null;
    }
    const user = note?.backend_user;
    const value = [user?.first_name?.trim(), user?.last_name?.trim()].filter(Boolean).join(' ');
    return value ? {id, value} : null;
}

/** Every note and reply in a list of top-level notes. */
function* walkNotes(notes: any[]): Iterable<any> {
    for (const note of notes || []) {
        if (!note) {
            continue;
        }
        yield note;
        for (const reply of note.childNote || []) {
            if (reply) {
                yield reply;
            }
        }
    }
}

/**
 * Apply a change to one surface's own copy of its notes. Returns true when
 * anything was changed, so the caller knows whether to repaint.
 */
export function applyNoteStateChange(notes: any[], change: NoteStateChange): boolean {
    let changed = false;
    for (const note of walkNotes(notes)) {
        if (change.kind === 'read') {
            for (const n of notificationsOf(note)) {
                if (sameId(n?.id, change.notificationId) && Number(n.status) !== Number(change.status)) {
                    n.status = change.status;
                    changed = true;
                }
            }
        } else if (sameId(note.id, change.noteId)) {
            changed = applyReaction(note, change) || changed;
        }
    }
    return changed;
}

/** Add or remove one user's reaction on a note, idempotently. */
export function applyReaction(note: any, change: Extract<NoteStateChange, { kind: 'reaction' }>): boolean {
    const reactions: any[] = note.note_reaction ?? note.noteReactions ?? [];
    const mine = (r: any) => sameId(r?.backend_user_id ?? r?.backend_user?.id, change.userId);
    if (change.liked) {
        if (reactions.some(mine)) {
            return false;
        }
        const row = change.reaction ?? {id: null, backend_user_id: change.userId};
        // A NEW array: OnPush hosts and `track` both compare by reference.
        note.note_reaction = [...reactions, {
            ...row,
            backend_user_id: row.backend_user_id ?? change.userId,
            backend_user: row.backend_user ?? change.user,
        }];
        return true;
    }
    if (!reactions.some(mine)) {
        return false;
    }
    note.note_reaction = reactions.filter((r: any) => !mine(r));
    return true;
}

/**
 * The note-state channel shared by every notes surface, plus the one
 * read/unread write they all use.
 */
@Injectable({providedIn: 'root'})
export class NoteStateService {
    private readonly changesSubject = new Subject<NoteStateChange>();

    /** Every like and read/unread change made anywhere in the console. */
    readonly changes$: Observable<NoteStateChange> = this.changesSubject.asObservable();

    constructor(
        private readonly notificationService: NotificationService,
        private readonly subscribersService: SubscribersService,
        private readonly alertService: AlertService,
    ) {
    }

    publish(change: NoteStateChange): void {
        this.changesSubject.next(change);
    }

    /** Publish a like / unlike that has been saved. */
    publishReaction(noteId: number, user: any, liked: boolean, reaction?: any): void {
        if (noteId == null || user?.id == null) {
            return;
        }
        this.publish({kind: 'reaction', noteId, userId: user.id, liked, reaction, user});
    }

    /**
     * Mark a note read or unread for the reader — `PATCH notification/{id}` on
     * each of their notifications about it, the call Quote Details has always
     * made. Patches the note in place, publishes each new status, refreshes the
     * header's unread badge and confirms with the same toast as before.
     *
     * `silent` skips the toast, for the automatic mark-read that follows a like.
     */
    setNoteRead(note: any, userId: any, read: boolean, silent = false): Observable<void> {
        const status = read ? Constants.NOTIFICATION_STATUS_ACTIVE : Constants.NOTIFICATION_STATUS_INACTIVE;
        const targets = myNoteNotifications(note, userId)
            .filter((n: any) => n?.id != null && Number(n.status) !== status);
        if (!targets.length) {
            return of(undefined);
        }
        return forkJoin(targets.map((n: any) => this.setNotificationStatus(n.id, status, Constants.NOTE_NOTIFICATION).pipe(
            tap((saved: number) => {
                n.status = saved;
            })
        ))).pipe(
            tap(() => {
                if (!silent) {
                    this.alertService.success(read
                        ? Constants.NOTIFICATION_MARKED_AS_READ_SUCCESS_MESSAGE
                        : Constants.NOTIFICATION_MARKED_AS_UNREAD_SUCCESS_MESSAGE);
                }
            }),
            map(() => undefined),
        );
    }

    /**
     * Notifications for a saved reply — Quote Details' pair
     * (`internal-notes.sendNotification` + `sendReplyNotification`):
     *
     *  - the parent note's author gets "replied to your note"
     *    (`parent_note_id` set) — INCLUDING when that is the replier, exactly
     *    as Quote Details does (its self-skip is commented out). That
     *    notification is what makes a reply on your own note unread for you,
     *    with Mark as read / unread on it;
     *  - every OTHER @mentioned user gets a mention. The parent author is left
     *    out of the mentions because the reply notification already reached
     *    them — which is what keeps the auto-mention from notifying them twice.
     *
     * Failures are swallowed: a notification must never fail the reply.
     *
     * Resolves with the notification rows it wrote, once every one has been
     * written. The caller shows the new reply WITH them, in the same paint as
     * the "added" toast — so a reply that is unread for the reader (your own
     * note's reply, a mention of you) shows its wash and Mark as read the
     * moment it appears, instead of popping in after a later reload.
     */
    notifyReply(reply: any, parentNote: any, selectedUsers: any[], editorContent: string, fromUserId: any): Promise<any[]> {
        const parentAuthorId = parentNote?.backend_user_id ?? parentNote?.backend_user?.id;
        const sends: Observable<any>[] = [];
        const send = (body: any) => sends.push(this.notificationService.sendNotification(body).pipe(
            catchError(() => of(null))));

        const messagePart = (editorContent || '').replace(/{{mention:([\w\s]+?)}}/g, '').trim();
        (Array.isArray(selectedUsers) ? selectedUsers : [])
            .filter((user: any) => user?.id != null && !sameId(user.id, parentAuthorId))
            .forEach((user: any) => send({
                message: `<span class="mention">@${user.value}</span> ${messagePart}`,
                note_id: reply.id,
                from_user_id: fromUserId,
                type: Constants.NOTE_NOTIFICATION,
                to_user_id: user.id,
            }));

        if (parentAuthorId != null) {
            send({
                message: reply.note,
                note_id: reply.id,
                parent_note_id: parentNote.id,
                from_user_id: fromUserId,
                type: Constants.NOTE_NOTIFICATION,
                to_user_id: parentAuthorId,
            });
        }
        if (!sends.length) {
            return Promise.resolve([]);
        }
        return new Promise<any[]>(resolve => forkJoin(sends).subscribe({
            next: (rows: any[]) => {
                this.subscribersService.updateUnreadNotification('0');
                // The v3 create echoes the saved columns but not `status`, which
                // the database defaults — a new notification is unread.
                resolve(rows.filter((row: any) => row?.id != null)
                    .map((row: any) => ({status: Constants.NOTIFICATION_STATUS_INACTIVE, ...row})));
            },
            error: () => resolve([]),
        }));
    }

    /**
     * `PATCH notification/{id}` and publish the stored status. The one write
     * behind every read/unread control — a note's, the drawer's, the page's.
     */
    setNotificationStatus(notificationId: number, status: number, type: string): Observable<number> {
        return this.notificationService.markNotificationAsRead({status, type}, notificationId).pipe(
            map((response: any) => Number(response?.data?.status ?? status)),
            tap((saved: number) => {
                this.publish({kind: 'read', notificationId, status: saved});
                // The header re-counts its badge on this ping.
                this.subscribersService.updateUnreadNotification('0');
            }),
        );
    }
}
